export type UserType = 'admin' | 'labeller' | 'trainee';

export interface User {
  id: string;
  userType: UserType;
  firstName: string;
  lastName: string;
  email: string;
}

export interface Essay {
  id: string;
  content: string;
  learn_cycle_batch: string;
  paper_number: number;
}

export interface Purpose {
  id: string;
  name: string;
  creationDate: string;
  modifiedDate: string;
}

export interface EssayPurpose {
  id: string;
  essayId: string;
  purposeId: string;
  creationDate: string;
}

export interface CriteriaPurpose {
  id: string;
  criteriaId: string;
  purposeId: string;
  creationDate: string;
}

export type Granularity = 'sentence' | 'paragraph' | 'essay';

export interface Criteria {
  id: string;
  name: string;
  granularity: Granularity;
  creationDate: string;
  modifiedDate: string;
  // Null while a criterion is being created: the criterion is inserted first so its
  // policy can carry a criteriaId (and therefore a version) at insert.
  // See docs/adr/0001-policy-versions-and-eval-eligibility.md#versioning.
  policyId: string | null;
  isArchived?: boolean;
}

export interface Policy {
  id: string;
  text: string;
  creationDate: string;
  criteriaId: string;
  // Assigned at insert as max(version)+1 for the Criterion. Still nullable for
  // legacy rows until migration 15. See docs/adr/0001-policy-versions-and-eval-eligibility.md.
  version?: number | null;
}

export interface Transcription {
  id: string;
  essayId: string;
  content: string;
  versionNameId: string;
  versionName: string;
  creationDate: string;
  modifiedDate: string;
}

export type AssignmentStatus = 'open' | 'started';

export interface CriterionTranscriptionAssignment {
  id: string;
  transcriptionId: string;
  criteriaId: string;
  userId: string;
  assignedDate: string;
  assignedBy: string;
  status: AssignmentStatus;
  startedAt: string | null;
  done: boolean;
}

export interface AssignmentHistoryAssignment {
  userId: string;
  userName: string;
  assignedDate: string;
  assignedBy: string;
  assignedByName: string;
  status: AssignmentStatus;
  done: boolean;
}

export interface AssignmentHistoryLabel {
  userId: string;
  userName: string;
  firstLabelledAt: string | null;
  lastLabelledAt: string | null;
  labelCount: number;
}

export interface AssignmentHistoryEntry {
  transcriptionId: string;
  assignments: AssignmentHistoryAssignment[];
  labels: AssignmentHistoryLabel[];
}

export interface AssignmentHistoryResponse {
  criteriaId: string;
  entries: AssignmentHistoryEntry[];
}

export interface CandidateTranscription {
  transcriptionId: string;
  essayId: string;
  paperNumber: number;
  batchName: string;
  versionName: string;
  isGroundTruth: boolean;
  hasAiMarks: boolean;
  purposeNames: string[];
  yearGroup: number | null;
  assignedUserIds: string[];
  assignedUserNames: string[];
}

export interface CandidateTranscriptionFilters {
  purposeId?: string[];
  yearGroups?: number[];
  onlyGroundTruth?: boolean;
  preferGroundTruth?: boolean;
}

export interface BulkAssignmentResponse {
  createdCount: number;
  assignments: CriterionTranscriptionAssignment[];
}

export type LabelType =
  | 'positive'
  | 'negative'
  | 'both'
  | 'uncertain'
  | 'unidentifiable'
  | 'no attempt';

export interface Label {
  id: string;
  transcriptionId: string;
  policyId: string;
  criteriaId: string;
  essayContentFragment: string | null;
  fragmentIndex?: number | null;
  type: LabelType;
  reason: string | null;
  createdBy: string;
  createdByRole: UserType;
  createdDate: string;
}

export interface UserVsGtCandidate {
  transcriptionId: string;
  paperNumber: number;
  sentence: string;
  granularity?: Granularity;
  userLabel: ReviewedClassification;
  userReason: string;
  groundTruthLabel: ReviewedClassification;
  groundTruthReviewerCount: number;
  groundTruthReasons: DisagreementHumanReason[];
  alreadyReviewed: boolean;
  review: DisagreementCandidateReview | null;
}

export interface UserVsGtCount {
  criteriaId: string;
  criteriaName: string;
  total: number;
  unresolved: number;
  comparedCount: number;
}

export interface UserVsGtCountsResponse {
  counts: UserVsGtCount[];
  omissions: DisagreementReviewOmissions;
}

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export interface SelectedFragment {
  text: string;
  index: number;
}

export type ReviewedClassification = 'positive' | 'negative' | 'both' | 'uncertain' | 'no attempt';

export const REVIEWED_CLASSIFICATIONS: ReviewedClassification[] = [
  'positive',
  'negative',
  'both',
  'uncertain',
  'no attempt',
];

export type ReviewedOutputSource = 'output_review' | 'disagreement_review';

export interface ReviewedOutput {
  id: string;
  criteriaId: string;
  policyId: string;
  stylusTranscriptionId: string;
  sentence: string;
  fragmentIndex?: number | null;
  originalClassification: ReviewedClassification;
  correctedClassification: ReviewedClassification;
  reason: string;
  source: ReviewedOutputSource;
  reviewedBy: string;
  reviewedAt: string;
}

export interface DisagreementHumanReason {
  userId: string;
  reason: string;
  label?: ReviewedClassification;
  counted?: boolean;
  notCountedReason?: string;
}

export interface DisagreementCandidateReview {
  id: string;
  correctedClassification: ReviewedClassification;
  reason: string;
  reviewedAt: string;
  fragmentIndex?: number | null;
}

export type GroundTruthSource = 'label_consensus' | 'previous_reviewed_output';

export interface DisagreementCandidate {
  transcriptionId: string;
  paperNumber: number;
  sentence: string;
  fragmentIndex?: number | null;
  granularity?: Granularity;
  humanLabel: ReviewedClassification;
  humanReviewerCount: number;
  humanReviewerIds?: string[];
  humanReasons: DisagreementHumanReason[];
  aiLabel: ReviewedClassification;
  aiReason: string;
  groundTruthSource: GroundTruthSource;
  previousReview: DisagreementCandidateReview | null;
  alreadyReviewed: boolean;
  review: DisagreementCandidateReview | null;
  hasDisagreement?: boolean;
}

export interface DisagreementCount {
  criteriaId: string;
  total: number;
  unresolved: number;
  comparedCount: number;
}

export interface DisagreementReviewOmissions {
  unlinkedTranscriptions: number;
  conflictingPapers: number;
  missingJudgements?: number;
  unresolvedEntities?: number;
}

export interface DisagreementCountsResponse {
  counts: DisagreementCount[];
  omissions: DisagreementReviewOmissions;
}

export type PolicySuggestionStatus =
  | 'pending'
  | 'accepted'
  | 'dismissed'
  | 'stale'
  | 'feedback_deleted';

export interface LinkedReview {
  id: string;
  sentence: string;
  originalClassification: ReviewedClassification;
  correctedClassification: ReviewedClassification;
  reason: string;
}

export interface OutstandingPolicyFeedback extends LinkedReview {
  policyId: string;
  policyVersion: number | null;
  supportsCurrentSuggestion: boolean;
}

export type SuggestionEdit =
  | { id: string; type: 'replace'; start: number; end: number; find: string; replacement: string }
  | { id: string; type: 'delete'; start: number; end: number; find: string }
  | { id: string; type: 'insert'; start: number; end: number; after: string; text: string };

export interface PolicySuggestion {
  id: string;
  criteriaId: string;
  policyId: string;
  suggestedPolicyText: string;
  suggestedEdits: SuggestionEdit[];
  contradictionReason: string;
  conflictingText: string[];
  status: PolicySuggestionStatus;
  createdAt: string;
  reviewedOutputIds: string[];
  linkedReviews: LinkedReview[];
  outstandingFeedback: OutstandingPolicyFeedback[];
}

export type PolicySuggestionGenerateResult =
  | PolicySuggestion
  | { noSuggestion: true; outstandingFeedback: OutstandingPolicyFeedback[] };

export interface LinkedModerationSentence {
  sentence: string;
  label: string | null;
  reason: string;
}

export interface LinkedModeration {
  airtableRecordId: string;
  originalComment: string | null;
  contextForChange: string | null;
  essay: string | null;
  sentences: LinkedModerationSentence[];
}

export interface ModerationPolicySuggestion {
  id: string;
  criteriaId: string;
  policyId: string;
  suggestedPolicyText: string;
  suggestedEdits: SuggestionEdit[];
  contradictionReason: string;
  conflictingText: string[];
  status: PolicySuggestionStatus;
  createdAt: string;
  linkedModerations: LinkedModeration[];
}

export interface AirtableModeration {
  airtableRecordId: string;
  criteriaId: string;
  policyId: string;
  judgementRunId: string;
  stylusJudgementId: string;
  reasonForChange: string | null;
  contextForChange: string | null;
  judgement: string | null;
  createdAt: string | null;
  commentType: string | null;
  originalComment: string | null;
  isFlaggedInaccurate: boolean;
}

export type AiJudgementSourceKind = 'production' | 'eval';

export interface AiJudgementSource {
  kind: AiJudgementSourceKind;
  markingRunId: string | null;
}

export interface EvalMarkingRun {
  id: string;
  name: string | null;
  createdAt: string | null;
  labellingCriterionIds: string[];
}

export interface EvalJudgementSourceCatalog {
  runs: EvalMarkingRun[];
  productionFrom: string | null;
  productionTo: string | null;
}

export const PRODUCTION_JUDGEMENT_SOURCE: AiJudgementSource = {
  kind: 'production',
  markingRunId: null,
};

export const EVAL_JUDGEMENT_SOURCE: AiJudgementSource = {
  kind: 'eval',
  markingRunId: null,
};

export interface StylusTranscriptionSummary {
  id: string;
  contentPreview: string;
}

export interface StylusSentenceJudgement {
  sentence: string;
  label: 'positive' | 'negative' | 'both' | null;
  reason: string;
}

export interface StylusEntityJudgement {
  index: number;
  text: string;
  /** Legacy sentence-shaped alias retained during the entity migration. */
  sentence: string;
  label: 'positive' | 'negative' | 'both' | null;
  reason: string;
}

export interface StylusJudgement {
  transcriptionId: string;
  content: string;
  entities?: StylusEntityJudgement[];
  /** Legacy alias retained for sentence-granularity consumers. */
  sentences: StylusSentenceJudgement[];
  granularity?: Granularity;
  entityWarnings?: string[];
  excludedEntityCount?: number;
  policyId: string | null;
}

export interface GroundTruthCoverageRow {
  id: string;
  name: string;
  granularity: Granularity;
  purposes: { id: string; name: string }[];
  labelCount: number;
  distinctTranscriptions: number;
  labelledTranscripts: number;
  latestLabelDate: string | null;
  labelsByType: Partial<Record<LabelType, number>>;
  implicitNaCount: number;
}

export interface GroundTruthCoverageTotals {
  distinctTranscriptions: number;
  labelledTranscripts: number;
}

export interface GroundTruthCoverageResponse {
  criteria: GroundTruthCoverageRow[];
  totals: GroundTruthCoverageTotals;
  appliedFilters: Record<string, unknown>;
}

export interface GroundTruthCoverageFilters {
  criteriaId?: string[];
  granularity?: Granularity[];
  purposeId?: string[];
  createdFrom?: string;
  createdTo?: string;
  createdBy?: string[];
}

export type ConsensusMode = 'strict' | 'lenient' | 'inclusive' | 'any';

export interface ConfusionCounts {
  tp: number;
  fp: number;
  fn: number;
  tn: number;
}

export interface ConfusionCell extends ConfusionCounts {
  precision: number | null;
  recall: number | null;
  f1: number | null;
}

export type EvalClass = 'positive' | 'negative' | 'both' | 'no attempt';
export type MarkingReadiness =
  | 'Ready for review'
  | 'Needs improvement'
  | 'Meets standard'
  | 'Needs attention'
  | 'Insufficient evidence';

export interface MarkingEvidenceThresholds {
  minComparedCases: number | null;
  minDistinctTranscriptions: number | null;
}

export interface MarkingMetricsSettings {
  exactAgreementThreshold: number;
  macroF1Threshold: number;
  evidenceThresholds: Record<Granularity, MarkingEvidenceThresholds>;
  gradientAmberMargin: number;
  minClassSupport: number;
  changedBy: string | null;
  changedAt: string | null;
}

export interface EvaluationCoverageCount {
  pairs: number;
  cases: number;
}

export type EvaluationCoverage = Record<
  'current' | 'missingEval' | 'outdatedEval' | 'invalidEval' | 'identityUnresolved',
  EvaluationCoverageCount
>;

export interface MarkingClassMetrics extends ConfusionCell {
  support: number;
}

export interface MarkingMetricsRow {
  id: string;
  name: string;
  granularity: Granularity;
  isArchived?: boolean;
  purposes: { id: string; name: string }[];
  liveState: 'live' | 'inactive' | 'not markable' | 'unlinked';
  assignmentCount: number;
  disagreementReviewRoundCount: number;
  unresolvedDisagreementCount: number;
  availableGroundTruth: number;
  comparedCount: number;
  distinctComparedTranscriptions: number;
  exactAgreement: number | null;
  macroPrecision: number | null;
  macroRecall: number | null;
  macroF1: number | null;
  representedClassCount: number;
  scoredClassCount: number;
  perClass: Record<EvalClass, MarkingClassMetrics>;
  evidence: MarkingEvidenceThresholds & { sufficient: boolean };
  performancePasses: boolean;
  readiness: MarkingReadiness;
  evaluationCoverage: EvaluationCoverage;
  evalUpdateRequired: boolean;
  /** Policy Versions in `unjudged` eligibility; their Labels do not become Eval Ground Truth. */
  unjudgedPolicyVersionCount: number;
  /** Labels on those unjudged Policy Versions. */
  unjudgedLabelCount: number;
  groundTruthProvenance: {
    reviewedOutputCount: number;
    explicitLabelCount: number;
    implicitNoAttemptCount: number;
    staleEligibilityCaseCount: number;
    conflictingCount: number;
    unresolvedIdentityCount: number;
  };
  evalProvenance: {
    contributingRunCount: number;
    oldestSelectedAt: string | null;
    newestSelectedAt: string | null;
  };
}

export interface MarkingMetricsSummary {
  availableGroundTruth: number;
  comparedCount: number;
  exactAgreement: number | null;
  macroPrecision: number | null;
  macroRecall: number | null;
  macroF1: number | null;
  readinessCounts: Partial<Record<MarkingReadiness, number>>;
  evaluationCoverage: EvaluationCoverage;
  evalProvenance: {
    contributingRunCount: number;
    oldestSelectedAt: string | null;
    newestSelectedAt: string | null;
  };
}

export interface MarkingMetricsResponse {
  criteria: MarkingMetricsRow[];
  summary: MarkingMetricsSummary;
  settings: MarkingMetricsSettings;
}

export interface MarkingMetricsFilters {
  criterionQuery?: string;
  criteriaId?: string[];
  granularity?: Granularity[];
  purposeId?: string[];
  liveState?: MarkingMetricsRow['liveState'][];
  evidenceState?: ('sufficient' | 'insufficient' | 'unconfigured')[];
  readiness?: MarkingReadiness[];
  coverageState?: ('current' | 'missing' | 'outdated' | 'invalid' | 'unresolved')[];
  minAvailableGroundTruth?: number;
  minCompared?: number;
  minDisagreementReviews?: number;
  maxDisagreementReviews?: number;
  minExactAgreement?: number;
  maxExactAgreement?: number;
  minMacroPrecision?: number;
  minMacroRecall?: number;
  minMacroF1?: number;
  maxMacroF1?: number;
  perClassMinF1?: Partial<Record<EvalClass, number>>;
  evalFrom?: string;
  evalTo?: string;
  includeArchived?: boolean;
}

// --- Policy-version comparison and eval eligibility -------------------------------
// docs/specs/policy-version-comparison.md

/** Derived, never stored. `unjudged` is a backlog item; `excluded` is a decision. */
export type EligibilityStatus =
  | 'current'
  | 'included'
  | 'excluded'
  | 'unjudged'
  | 'no_labels'
  | 'unusable';

export interface PolicyVersionState {
  policyId: string;
  version: number;
  currentVersion: number;
  isCurrent: boolean;
  labelCount: number;
  eligible: boolean;
  /** The grant was made against an earlier current version. Never revokes eligibility. */
  stale: boolean;
  status: EligibilityStatus;
  /**
   * This is the version Stylus marks with, held by an admin. Orthogonal to eligibility:
   * a pinned version can be excluded and an eligible one need not be pinned.
   * See docs/specs/policy-version-pinning.md.
   */
  isPinned: boolean;
  creationDate: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decidedAgainstVersion: number | null;
  /** The reference policy row itself, so a decision traces back to the text read. */
  decidedAgainstPolicyId: string | null;
  reason: string | null;
  /** Only present on the criterion-detail endpoint. */
  text?: string;
}

/** A criterion's pin: which rubric Stylus marks with, and who held it there. */
export interface PolicyPin {
  policyId: string;
  version: number;
  pinnedBy: string;
  pinnedByName: string | null;
  pinnedAt: string | null;
  reason: string | null;
}

export interface CriterionEligibilitySummary {
  criteriaId: string;
  criterionName: string;
  granularity: Granularity;
  versionsTotal: number;
  versionsWithLabels: number;
  labelsTotal: number;
  currentVersion: number;
  latestVersionDate: string | null;
  versionsToJudge: number;
  labelsAtStake: number;
  labelsRecovered: number;
  labelsEligible: number;
  staleCount: number;
  /** Every version -- current included -- has been ruled ineligible. Survives a refresh:
   *  read straight off the eligibility rows rather than tracked as local UI state. */
  allDisregarded: boolean;
  /** Every version's UUID with its number, so the filter can match a pasted policy id. */
  policyVersions: { policyId: string; version: number }[];
  /**
   * Lowest cosine to the current version among this criterion's unjudged versions --
   * triage effort, NOT confidence. High similarity means the diff is short, never that
   * the change is safe: the two highest-similarity high-value criteria in this corpus are
   * the two #9 found had changed what is creditable. Null when no unjudged version has a
   * cached embedding.
   */
  lowestSimilarity: number | null;
  /** How many unjudged versions actually had a vector, so "(4)" can be honest. */
  unjudgedScored: number;
  versionsJudged: number;
  labelsRecoverable: number;
  versionsRecoverable: number;
  /** The version Stylus marks with, when held. Null means the criterion follows current. */
  pinnedVersion: number | null;
}

export interface VersionSimilarity {
  policyIdA: string;
  policyIdB: string;
  versionA: number;
  versionB: number;
  /** Triage ordering only -- it never writes a verdict. */
  cosine: number;
}

export interface PolicyVersionLabel {
  id: string;
  essayContentFragment: string | null;
  type: string;
  reason: string | null;
  createdDate: string | null;
}

export interface CriterionComparison {
  criteriaId: string;
  criterionName: string;
  granularity: Granularity;
  versions: PolicyVersionState[];
  similarities: VersionSimilarity[];
  labelsByPolicyId: Record<string, PolicyVersionLabel[]>;
  pin: PolicyPin | null;
}

export interface EligibilityProgress {
  criteriaTotal: number;
  criteriaNeedingJudgement: number;
  versionsToJudge: number;
  labelsAtStake: number;
  labelsRecovered: number;
  labelsEligible: number;
  labelsTotal: number;
  staleVersions: number;
  versionsJudged: number;
  versionsRecoverable: number;
  labelsRecoverable: number;
}

export type BulkEligibilityAction =
  | 'include_all'
  | 'disregard_all'
  | 'reaffirm_all'
  /** Deletes the rows entirely, so versions revert to unjudged rather than excluded. */
  | 'clear_all';
